import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // HttpOnly cookie is sent automatically with withCredentials: true.
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const value = useMemo(() => {
    const login = async (email, password) => {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return data.user;
    };

    const signup = async (payload) => {
      const { data } = await api.post("/auth/signup", payload);
      // When verification is required, do NOT set the user (no cookie yet).
      // Caller should move to the "enter code" step. Otherwise (legacy path)
      // treat the response like an immediate login.
      if (!data?.verification_required) {
        setUser(data.user);
      }
      return data;
    };

    const verifySignupOtp = async (email, code) => {
      const { data } = await api.post("/auth/signup/verify", { email, code });
      setUser(data.user);
      return data.user;
    };

    const resendSignupOtp = async (email) => {
      const { data } = await api.post("/auth/signup/resend", { email });
      return data;
    };

    const requestLoginOtp = async (email) => {
      const { data } = await api.post("/auth/otp/request", { email });
      return data;
    };

    const loginWithOtp = async (email, code) => {
      const { data } = await api.post("/auth/otp/verify", { email, code });
      setUser(data.user);
      return data.user;
    };

    const logout = async () => {
      try {
        await api.post("/auth/logout");
      } catch (err) {
        // Clearing the cookie is a best-effort; surface errors only in dev
        // so silent failures are still debuggable without leaking to prod users.
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("auth/logout failed:", err?.response?.data?.detail || err?.message);
        }
      }
      localStorage.removeItem("menumojo_active_restaurant");
      setUser(null);
    };

    return { user, loading, login, signup, logout, setUser, requestLoginOtp, loginWithOtp, verifySignupOtp, resendSignupOtp };
  }, [user, loading]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
